import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Space, Button, Col, Row, Modal, Drawer, Tag, Popover, Table } from "antd";
import { BiCloudDownload, BiCoinStack, BiFilterAlt, BiTransfer } from "react-icons/bi";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import {
  deleteStaff,
  getStaffList,
} from "../../core/store/reducers/staff.slice";
import CreateEditStaffComponent from "./components/create-edit-staff.component";
import {
  accessAllowed,
  formatCost,
  formatCost1,
  formatDate,
  showNotification,
} from "../../shared/utils/utils";
import moment from "moment";
import { Client } from "../../core/api-client";
import {
  API_GET_ALL_DEPARTMENT_LIST,
  API_GET_ALL_PROJECT_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_ALL_VENDOR_LIST,
  API_GET_STAFF_LIST,
  API_GET_PROJECT_DETAILS,
} from "../../shared/routes/private.routes";
import StaffBulkImportComponent from "./components/staff-bulk-import.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { RiCurrencyLine } from "react-icons/ri";
import StaffSalaryRevisionComponent from "./components/staff-salary-revision.component";
import { allPermissions } from "../../shared/constants/constants";
import Papa from "papaparse";
import { requestGetByModuleCommon } from "../../core/store/reducers/master.slice";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import StaffAssignmentComponent from "../project/components/staff-assignment.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const FILTER_FIELD_LABELS: Record<string, string> = {
  staffName: "Name",
  staffStatusId: "Status",
  departmentId: "Department",
  projectId: "Project",
  lineManagerId: "Line Manager",
};

function buildFilterValueText(
  key: string,
  value: any,
  lists: {
    departmentList: any[];
    projectList: any[];
    staffDropdownList: any[];
  }
): string {
  const { departmentList, projectList, staffDropdownList } = lists;
  switch (key) {
    case "staffName":
      return String(value).trim();
    case "staffStatusId":
      return Array.isArray(value) ? value.join(", ") : String(value);
    case "departmentId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            departmentList.find((d) => d.departmentId === id)
              ?.departmentName ?? id
        )
        .join(", ");
    }
    case "projectId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            projectList.find((p) => p.projectId === id)?.projectName ?? id
        )
        .join(", ");
    }
    case "lineManagerId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            staffDropdownList.find((s) => s.staffId === id)?.staffName ?? id
        )
        .join(", ");
    }
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

const CLEAR_FIELD_VALUES: Record<string, any> = {
  staffName: undefined,
  staffStatusId: [],
  departmentId: [],
  projectId: [],
  lineManagerId: [],
};

type CurrentMonthAllocationRow = {
  projectId: string;
  projectName: string;
  percentAllocation: number;
  accountName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

type CurrentMonthProjectCellProps = {
  record: any;
  canOpenResourceAssignment?: boolean;
  onOpenProjectResourceAssignment?: (projectId: string) => void;
};

/** Project column: top allocation this month + popover with full breakdown from staff list API. */
function CurrentMonthProjectCell({
  record,
  canOpenResourceAssignment,
  onOpenProjectResourceAssignment,
}: CurrentMonthProjectCellProps) {
  const allocations = record.currentMonthAllocationProjects as
    | CurrentMonthAllocationRow[]
    | undefined;
  const topName = record.currentMonthTopProjectName as string | undefined;
  const fallback = record.projectName as string | undefined;
  const label = (topName || fallback || "").trim();
  const monthLabel = moment().format("MMM YYYY");

  const popoverContent =
    allocations && allocations.length > 0 ? (
      <div style={{ maxWidth: 720 }}>
        <div
          style={{
            marginBottom: 8,
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          Project allocations ({monthLabel})
        </div>
        <Table<CurrentMonthAllocationRow>
          size="small"
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(row, index) => `${row.projectId}-${index}`}
          dataSource={allocations}
          columns={[
            {
              title: "Client",
              dataIndex: "accountName",
              ellipsis: true,
              width: 140,
              render: (v: string | null | undefined) => v?.trim() || "—",
            },
            {
              title: "Project",
              dataIndex: "projectName",
              ellipsis: true,
              width: 160,
            },
            {
              title: "% allocation",
              width: 96,
              align: "right",
              render: (_: unknown, row) =>
                `${Number(row.percentAllocation ?? 0)}%`,
            },
            {
              title: "Project start",
              width: 118,
              render: (_: unknown, row) =>
                row.startDate
                  ? formatDate(row.startDate, "DD-MMM-YYYY")
                  : "—",
            },
            {
              title: "Project end",
              width: 118,
              render: (_: unknown, row) =>
                row.endDate ? formatDate(row.endDate, "DD-MMM-YYYY") : "—",
            },
            ...(canOpenResourceAssignment && onOpenProjectResourceAssignment
              ? [
                  {
                    title: "",
                    key: "resourceAssignment",
                    width: 44,
                    align: "center" as const,
                    render: (_: unknown, row: CurrentMonthAllocationRow) => (
                      <Button
                        type="link"
                        size="small"
                        className="p-0"
                        icon={<UserOutlined />}
                        title="Resource assignment"
                        aria-label="Open resource assignment for this project"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenProjectResourceAssignment(row.projectId);
                        }}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>
    ) : null;

  return (
    <Space size={4} align="center" wrap>
      <span>{label || "—"}</span>
      {popoverContent ? (
        <Popover content={popoverContent} trigger="click" placement="leftTop">
          <Button
            type="text"
            size="small"
            className="p-0"
            icon={
              <InfoCircleOutlined style={{ color: "var(--primary)" }} />
            }
            aria-label={`Project allocations for ${monthLabel}`}
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      ) : null}
    </Space>
  );
}

const ManageStaffPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingStaff, staffList, pagination } = useSelector(
    (state: RootState) => state.staff
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [staffIdToDelete, setStaffIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { user } = useSelector((state: RootState) => state.auth);
  // List local state
  const [staffListData, setStaffListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [staffDropdownList, setStaffList] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [openSalaryRevision, setOpenSalaryRevision] = useState(false);
  const [employeeTypes, setEmployeeTypes] = useState<any[]>([]);
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [openStaffAssignment, setOpenStaffAssignment] = useState(false);
  const [resourceAssignmentProject, setResourceAssignmentProject] =
    useState<any>(null);

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  useEffect(() => {
    loadList();
  }, [filters, sorter]);
  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getStaffList({ params: data, body: filter }));
  };

  const handleOpenStaffAssignmentForProject = useCallback(
    async (projectId: string) => {
      try {
        const response = await Client.get(API_GET_PROJECT_DETAILS + projectId);
        const entity = response?.data?.data?.[0];
        if (entity) {
          setResourceAssignmentProject(entity);
          setOpenStaffAssignment(true);
        } else {
          showNotification("Project not found", "error");
        }
      } catch {
        showNotification("Could not load project", "error");
      }
    },
    []
  );

  const onCloseStaffAssignment = useCallback(() => {
    setOpenStaffAssignment(false);
    setResourceAssignmentProject(null);
  }, []);

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const bulkImportEntityListner = () => {
    setBulkImportOpen(true);
    // setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };
  useEffect(() => {
    setStaffListData(
      staffList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [staffList]);
  const onFilterApply = (values: any) => {
    setFilters(values); //filters
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: FILTER_FIELD_LABELS[key] || key,
        valueText: buildFilterValueText(key, filters[key], {
          departmentList,
          projectList,
          staffDropdownList,
        }),
      }));
  }, [filters, departmentList, projectList, staffDropdownList]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in CLEAR_FIELD_VALUES ? CLEAR_FIELD_VALUES[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );
  const onPageChange = (pageNumber: number) => {
    // loadList((pageNumber - 1) * pagination?.limit);
    loadList(pageNumber);
  };
  const hasAnyAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_STAFF) ||
    accessAllowed(myPermissions, allPermissions.DELETE_STAFF) ||
    accessAllowed(myPermissions, allPermissions.STAFF_ACTIVITY_LOGS) ||
    accessAllowed(myPermissions, allPermissions.VIEW_STAFF_SALARY_REVISION);
  const canProjectStaffAssignment = accessAllowed(
    myPermissions,
    allPermissions.PROJECT_STAFF_ASSIGNMENT
  );
  const columns = [
    {
      title: "Employee Id",
      dataIndex: "employeeId",
      sorter: false,
      key: "employeeId",
    },
    {
      title: "Staff Name",
      dataIndex: "staffName",
      sorter: false,
      key: "staffName",
    },
    {
      title: "Line Manager",
      dataIndex: "lineManager",
      sorter: false,
      key: "lineManager",
    },
    {
      title: "Status",
      dataIndex: "staffStatusId",
      sorter: false,
      key: "staffStatusId",
      render: (status: string) => {
        const color =
          status === "ACTIVE"
            ? "success"
            : status === "NOTICE PERIOD"
            ? "warning"
            : "red";
        return status ? <Tag color={color}>{status}</Tag> : "—";
      },
    },
    {
      title: "Employment Mode",
      dataIndex: "employmentModeName",
      sorter: false,
      key: "employmentModeName",
    },
    {
      title: "Role",
      dataIndex: "role",
      sorter: false,
      key: "role",
    },
    // {
    //   title: "Employee Type",
    //   dataIndex: "employeeType",
    //   sorter: false,
    //   key: "employeeType",
    // },
    // {
    //   title: "Department Name",
    //   dataIndex: "departmentName",
    //   sorter: false,
    //   key: "departmentName",
    // },
    {
      title: "Project",
      dataIndex: "projectName",
      sorter: false,
      key: "projectName",
      render: (_: unknown, record: any) => (
        <CurrentMonthProjectCell
          record={record}
          canOpenResourceAssignment={canProjectStaffAssignment}
          onOpenProjectResourceAssignment={handleOpenStaffAssignmentForProject}
        />
      ),
    },
    // {
    //   title: "CTC",
    //   dataIndex: "ctc",
    //   sorter: false,
    //   key: "ctc",
    //   render: (ctc: number, record: any) => (ctc ? formatCost1(ctc,record.salaryCurrency) : ""),
    // },
    // {
    //   title: "Date of Joining",
    //   dataIndex: "doj",
    //   sorter: false,
    //   key: "doj",
    //   render: (doj: string) => (doj ? formatDate(doj,"DD-MMM-YYYY ") : ""),
    // },
    // {
    //   title: "Last Working Day",
    //   dataIndex: "lwd",
    //   sorter: false,
    //   key: "lwd",
    //   render: (lwd: string) => (lwd ? formatDate(lwd,"DD-MMM-YYYY ") : ""),
    // },
    {
      title: "Entity",
      dataIndex: "entityName",
      sorter: false,
      key: "entityName",
    },
    ...(hasAnyAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space size="small">
                {accessAllowed(myPermissions, allPermissions.UPDATE_STAFF) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                    title="Edit"
                  />
                )}
                {/* {accessAllowed(myPermissions, allPermissions.DELETE_STAFF) && (
                  <Button
                    type="link"
                    danger
                    onClick={() => handleDeleteModal(record?.staffId)}
                    icon={<DeleteOutlined />}
                    title="Delete"
                  />
                )} */}
                {accessAllowed(
                  myPermissions,
                  allPermissions.STAFF_ACTIVITY_LOGS
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                    title="Activity log"
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.VIEW_STAFF_SALARY_REVISION
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleSalaryRevision(record)}
                    icon={<RiCurrencyLine />}
                    title="Salary revision"
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];
  const handleDelete = (staffId: string) => {
    dispatch(deleteStaff(staffId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Staff Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleDeleteModal = (staffId: string) => {
    setDeleteModalVisible(true);
    setStaffIdToDelete(staffId);
  };

  const onDeleteConfirm = () => {
    handleDelete(staffIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setStaffIdToDelete("");
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedStaffId(record.staffId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setSelectedStaffId("");
  };

  const handleSalaryRevision = (record: any) => {
    setOpenSalaryRevision(true);
    setSelectedStaffId(record.staffId);
    setSelectedEntity(record);
  };

  const onCloseSalaryRevision = () => {
    setOpenSalaryRevision(false);
    setSelectedStaffId("");
  };

  // const handleDelete = (staffId: string)=>{
  //     dispatch(deleteStaff(staffId)).then(
  //         (val: any) => {
  //           if (val?.payload?.data) {
  //             showNotification("Staff Deleted", "success");
  //             loadList();
  //           }
  //         }
  //       );
  // }

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };
  useEffect(() => {
    // dispatch(getList()).then((val: any) => {
    //   if (val?.payload?.data?.data?.length > 0) {
    //     setList(
    //       val.payload.data.data.map((val: any) => {
    //         return { label: val.name, value: val._id };
    //       })
    //     );
    //   }
    // });
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => {};

  useEffect(() => {
    fetchStaffList();
    fetchProjectList();
    fetchDepartmentList();
    fetchEmployeeTypes()
    fetchVendorList()
  }, []);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  const fetchProjectList = async () => {
    const response = await Client.get(API_GET_ALL_PROJECT_LIST);
    if (response?.data?.data) {
      setProjectList(response?.data?.data);
    } else {
      setProjectList([]);
    }
  };

  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartmentList(response?.data?.data);
    } else {
      setDepartmentList([]);
    }
  };

  const staffDropdownOptions = staffDropdownList.map((staff) => ({
    value: staff?.staffId,
    label: staff?.staffName,
  }));

  const projectDropdownOptions = projectList.map((project) => ({
    value: project?.projectId,
    label: project?.projectName,
  }));

  const departmentDropdownOptions = departmentList.map((department) => ({
    value: department?.departmentId,
    label: department?.departmentName,
  }));
  const fetchEmployeeTypes = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_employee_type" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setEmployeeTypes(response?.data.items);
      }
    } catch (error) {}
  };
  const fetchVendorList = async () => {
    const response = await Client.get(API_GET_ALL_VENDOR_LIST);
    if (response?.data?.data) {
      setVendorList(response?.data?.data);
    } else {
      setVendorList([]);
    }
  };
  const exportStaffList = async () => {
    try{
      const result = await Client.post(API_GET_STAFF_LIST, {}, {params: {limit: 1000}});
      const rows = result?.data?.data;
      console.log(rows);
      const rowData = rows?.map((item:any)=>{
        return {
          "Entity": item?.entityName,
          "Employee Id": item?.employeeId,
          "Employee Name": item?.staffName,
          "Status": item?.staffStatusId,
          "Doj": item?.doj ? moment(item?.doj).format("DD-MM-YYYY") : "",
          "Email ID": item?.email,
          "Client": item?.currentMonthTopAccountName ?? "-",
          "Project": item?.currentMonthTopProjectName ?? "-",
          "Designation": item?.designation,
          "Reporting Manager": item?.lineManager,
          "Employee Type": employeeTypes.find((employeeType:any)=>employeeType.id === item?.employeeType)?.name || "", // convert to text
          "Employment Mode": item?.employmentModeName,
          "Vendor Name (for contractors)": vendorList.find((vendor:any)=>vendor.vendorId === item?.vendorId)?.vendorName || "", // convert to text
          "LWD": item?.lwd ? moment(item?.lwd).format("DD-MM-YYYY") : "",
          "Department": item?.departmentName, 
          "Sub Department": item?.subDepartmentName, 
          "Role": item?.role,
        }
      })
      const csv = Papa.unparse(rowData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "staff_list.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    }catch(e){

    }
  };
  const formProps = {
    form: form,
    name: "manage-staff-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "staffName",
        label: "",
        placeholder: "Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "staffStatusId",
        label: "",
        placeholder: "Staff Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
          { value: "NOTICE PERIOD", label: "NOTICE PERIOD" },
        ],
      },
      {
        name: "departmentId",
        label: "",
        placeholder: "Department",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: departmentDropdownOptions,
      },
      {
        name: "projectId",
        label: "",
        placeholder: "Project",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: projectDropdownOptions,
      },
      {
        name: "lineManagerId",
        label: "",
        placeholder: "Line Manager",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: staffDropdownOptions,
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.STAFF_BULK_UPLOAD) && (
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={bulkImportEntityListner}
        >
          <span className="font-size-14">
            {!screenType?.isPortrait && "Staff "}Bulk import
          </span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_STAFF) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Staff"}
          </span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_STAFF) && (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportStaffList}
        >
          <span className="font-size-14">
            Export {!screenType?.isPortrait && "Staff List"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      {/* Page header card */}
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <TeamOutlined />
              </div>
              <h1 className="app-page-title">Manage Staff</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage your organization’s staff and assignments
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {/* Filters card */}
      {filterVisible ? (
        <div className="app-filter-bar">
          <div className="app-filter-title">
            <BiFilterAlt style={{ fontSize: 14 }} />
            Filters
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      ) : null}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* Table card */}
      <div className="app-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingStaff}
          dataSource={staffListData}
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={pagination.page}
          totalRows={pagination?.total}
          onPageChange={onPageChange}
          tableWidth="1500px"
        />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this staff member?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditStaffComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {bulkImportOpen && (
        <StaffBulkImportComponent
          onClose={() => setBulkImportOpen(false)}
          visible={bulkImportOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedStaffId}
            record={selectedEntity}
            module="Staff"
          />
        </Drawer>
      )}
      {openSalaryRevision && (
        <StaffSalaryRevisionComponent
          visible={openSalaryRevision}
          onClose={onCloseSalaryRevision}
          selectedEntity={selectedEntity}
        />
      )}
      {openStaffAssignment && resourceAssignmentProject && (
        <StaffAssignmentComponent
          visible={openStaffAssignment}
          onClose={onCloseStaffAssignment}
          selectedEntity={resourceAssignmentProject}
          onSubmit={() => loadList()}
        />
      )}
    </div>
  );
};
export default ManageStaffPage;
